import React from 'react';
import get from 'lodash/get';
import { MultiVariantProductCard } from '../../components/Bundle/MultiVariantProductCard';
import DefaultProductCard from '../../components/ProductCard';
import ThemedProductCard from '../../bundles-themed/components/ProductCard';
import {
  getPriceFromSellingPlanAllocations,
  getProductCurrentPrice,
  getProductPrice,
  isGlobalProductList,
  isProductAvailable,
} from '../../utils';
import { useGetIndividualProducts } from '../bundleUtils';
import {
  SectionsSection,
  SectionTitle,
  SectionLabel,
} from './SectionsComponents';
import {
  findSectionSettings,
  sortProductsBySelected,
  calculateErrorClassAndLabel,
  getKindOfLimit,
} from './utils';

interface SectionsProps {
  _awtId?: string;
  section: BabSection;
  sellingPlan?: string | number;
  onCardClick: (product) => void;
  addProduct: (
    sectionId: string,
    product: GlobalVariant | GlobalProduct,
  ) => void;
  removeProduct: (
    sectionId: string,
    product: GlobalVariant | GlobalProduct,
  ) => void;
  selectedProducts: Record<string, number>;
  setSelectedProducts: (
    sectionId: string,
    newSelectedProducts: Record<string, number>,
  ) => void;
  canAddProducts: boolean;
  BaBProductsToCartFlag: boolean;
  initialSelectedProducts: Record<string, number>;
  selectedProductsBySection: Record<string, number>;
  hideBaBSoldOutOptions: boolean;
  ignoreBaBOutOfStockAllowance: boolean;
  discount?: {
    type: string;
    value: number;
  };
  isVariantCollapsed: boolean;
  size: number;
  filteredProducts?: GlobalVariant[] | GlobalProduct[];
  hasSections: boolean;
  isClassicBab: boolean;
  isThemed?: boolean;
  ThemedSectionsContainer?: React.ComponentType<any>;
  ThemedCardContainer?: React.ComponentType<any>;
}

export const Sections = ({
  section,
  hasSections,
  sellingPlan,
  onCardClick,
  addProduct,
  removeProduct,
  selectedProducts,
  selectedProductsBySection,
  setSelectedProducts,
  canAddProducts,
  BaBProductsToCartFlag,
  initialSelectedProducts,
  hideBaBSoldOutOptions,
  ignoreBaBOutOfStockAllowance,
  discount,
  isVariantCollapsed,
  size,
  filteredProducts = [],
  isClassicBab,
  isThemed = false,
  ThemedSectionsContainer,
  ThemedCardContainer,
  _awtId,
}: SectionsProps) => {
  const productsInSection: GlobalVariant[] | GlobalProduct[] =
    useGetIndividualProducts({
      isVariantCollapsed,
      productsInfo: section.products,
      defaultIndividualProducts: section.individualProductsCollection,
      productsInCollection:
        section.productsInCollections.length > 0
          ? section.productsInCollections.flat()
          : section.individualProductsCollection,
    });

  const sortedProducts = sortProductsBySelected(
    productsInSection,
    initialSelectedProducts,
  ).filter(
    ({ id, productId, variants }) =>
      filteredProducts?.map(({ id }) => id).includes(id) ||
      filteredProducts?.map(({ id }) => id).includes(productId) ||
      variants?.some(
        (variant) =>
          filteredProducts?.map(({ id }) => id)?.includes(variant.id),
      ),
  );

  const settings = findSectionSettings(section.settings, size);

  const { errorClass, errorLabel } = calculateErrorClassAndLabel(
    selectedProductsBySection,
    settings,
  );

  const kindOfSectionLimit = getKindOfLimit(settings);
  const ProductCard = isThemed ? ThemedProductCard : DefaultProductCard;
  const SectionContainer =
    isThemed && ThemedSectionsContainer
      ? ThemedSectionsContainer
      : SectionsSection;

  return sortedProducts.length > 0 ? (
    <div
      id={`section-${section.id}`}
      key={`section-${section.id}`}
      className={`awt-section-with-limits ${kindOfSectionLimit} ${errorClass}`}
      data-error-label={errorLabel}
      role="tabpanel"
    >
      {hasSections && (
        <SectionTitle
          key={`title-${section.id}`}
          className="bundle-section__title"
          id={`title-${section.id}`}
          modifiers={['display']}
        >
          {section.name}
        </SectionTitle>
      )}
      {settings?.ErrorLabel && (
        <SectionLabel>{settings?.ErrorLabel}</SectionLabel>
      )}
      <SectionContainer
        key={section.id}
        role="grid"
        className="bundle_section bundle-section"
        id={`section-${section.id}`}
        sectionId={`section-${section.id}`}
      >
        {isGlobalProductList(sortedProducts) ? (
          <MultiVariantProductCard
            _awtId={_awtId}
            sellingPlan={sellingPlan}
            products={sortedProducts}
            onCardClick={onCardClick}
            addProduct={(product) => addProduct(section.id, product)}
            removeProduct={(product) => removeProduct(section.id, product)}
            selectedProducts={selectedProducts}
            setSelectedProducts={(newSelectedProducts) =>
              setSelectedProducts(section.id, newSelectedProducts)
            }
            canAddProducts={canAddProducts}
            baBProductsToCartFlag={BaBProductsToCartFlag}
            initialSelectedProducts={initialSelectedProducts}
            hideBaBSoldOutOptions={hideBaBSoldOutOptions}
            ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
            isClassicBab={isClassicBab}
            isThemed={isThemed}
            ThemedCardContainer={ThemedCardContainer}
            ProductCard={ProductCard}
          />
        ) : (
          sortedProducts.map((product) => {
            if (hideBaBSoldOutOptions && !product.available) {
              return null;
            }

            const currentPrice = getProductCurrentPrice(product);
            const price =
              getProductPrice(product, discount) ||
              getPriceFromSellingPlanAllocations(product, sellingPlan) ||
              currentPrice;

            const cardContent = (
              <ProductCard
                key={product.id}
                _awtId={`${_awtId}`}
                id={product.id as number}
                handle={product.handle}
                productId={product.productId}
                price={isClassicBab ? undefined : price}
                currentPrice={currentPrice}
                currency={get(window, ['Shopify', 'currency', 'active'], 'USD')}
                image={product.image as string}
                quantity={selectedProducts[product.id] || 0}
                subtitle={product.metafields?.bundle?.subtitle}
                title={product.name}
                available={isProductAvailable(
                  product,
                  {},
                  ignoreBaBOutOfStockAllowance,
                )}
                onIncrease={() =>
                  canAddProducts && addProduct(section.id, product)
                }
                onDecrease={() => removeProduct(section.id, product)}
                onCardClick={(productInfo) =>
                  onCardClick({
                    ...productInfo,
                    images: product.images,
                    productId: product.productId,
                    hasOneVariant: product.hasOnlyDefaultVariant,
                    media: product.media,
                  })
                }
                description={product.description}
              />
            );

            return isThemed && ThemedCardContainer ? (
              <ThemedCardContainer key={product.id} _awtId={`${_awtId}`}>
                {cardContent}
              </ThemedCardContainer>
            ) : (
              cardContent
            );
          })
        )}
      </SectionContainer>
    </div>
  ) : null;
};
